import { cn } from "@wa/ui";
import { useI18n, useT } from "../../i18n/I18nProvider";
import type { PortalLineItem } from "../../lib/portal";
import { fmtAmount } from "../../lib/format";

function lineQty(li: PortalLineItem): number {
  return typeof li.qty === "number" ? li.qty : 1;
}
function lineUnit(li: PortalLineItem): number {
  return typeof li.unitPriceCents === "number" ? li.unitPriceCents : 0;
}

/**
 * The lines of an invoice or a quote.
 *
 * Two drawings of the same rows, and only one is ever on screen: a table from
 * `md` up, a stack below it. Four columns do not fit a phone, and a table that
 * scrolls sideways hides the one column that matters - the amount - off the
 * edge. The hidden drawing is `display: none`, so a screen reader meets each
 * line once.
 *
 * The detail paragraph keeps its line breaks. It is usually a bullet list typed
 * by hand, and collapsing the newlines turns it into one unreadable sentence.
 */
export function LineItems({ lines, currency }: { lines: PortalLineItem[]; currency: string }) {
  const t = useT();
  const { locale } = useI18n();
  // Quantities are often fractional hours. Formatted like every other number on
  // the page, so 3.5 hours reads "3,5" beside "640,00 €" rather than "3.5".
  const qty = new Intl.NumberFormat(locale === "et" ? "et-EE" : "en-GB", { maximumFractionDigits: 3 });
  const money = (cents: number) => fmtAmount(cents, currency, locale);

  return (
    <>
      <div className="hidden md:block">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-foreground/70">
              <th scope="col" className="pb-2.5 pr-4 font-semibold">{t("portal.invoices.item")}</th>
              <th scope="col" className="px-4 pb-2.5 text-right font-semibold">{t("portal.invoices.qty")}</th>
              <th scope="col" className="px-4 pb-2.5 text-right font-semibold">{t("portal.invoices.unit")}</th>
              <th scope="col" className="pb-2.5 pl-4 text-right font-semibold">{t("portal.invoices.total")}</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((li, i) => (
              <tr key={i} className="border-b border-border align-top last:border-0">
                <td className="py-4 pr-4">
                  <span className="block font-medium">{li.description || "-"}</span>
                  {li.details && (
                    <span className="mt-1 block whitespace-pre-wrap text-foreground/70">{li.details}</span>
                  )}
                </td>
                <td className="px-4 py-4 text-right tabular-nums">{qty.format(lineQty(li))}</td>
                <td className="whitespace-nowrap px-4 py-4 text-right tabular-nums">{money(lineUnit(li))}</td>
                <td className="whitespace-nowrap py-4 pl-4 text-right font-semibold tabular-nums">
                  {money(lineQty(li) * lineUnit(li))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ul className="divide-y divide-border md:hidden">
        {lines.map((li, i) => (
          <li key={i} className="py-3.5 first:pt-0 last:pb-0">
            <p className="font-medium">{li.description || "-"}</p>
            {li.details && <p className="mt-1 whitespace-pre-wrap text-sm text-foreground/70">{li.details}</p>}
            <div className="mt-2 flex items-baseline justify-between gap-3 tabular-nums">
              <span className="text-sm text-foreground/70">
                {qty.format(lineQty(li))} × {money(lineUnit(li))}
              </span>
              <span className="font-semibold">{money(lineQty(li) * lineUnit(li))}</span>
            </div>
          </li>
        ))}
      </ul>
    </>
  );
}

/**
 * The sums under the lines: quiet rows, and the one that is the answer held
 * apart from them by a rule and its own weight.
 */
export function Totals({
  rows,
  className,
}: {
  rows: { label: string; value: string; strong?: boolean }[];
  className?: string;
}) {
  return (
    <dl className={cn("ml-auto w-full max-w-xs space-y-2 text-sm", className)}>
      {rows.map((row) => (
        <div
          key={row.label}
          className={cn(
            "flex items-baseline justify-between gap-4",
            row.strong ? "border-t border-border pt-3 font-bold first:border-0 first:pt-0" : "text-foreground/70",
          )}
        >
          <dt>{row.label}</dt>
          <dd className={cn("tabular-nums", row.strong && "font-display text-lg")}>{row.value}</dd>
        </div>
      ))}
    </dl>
  );
}
